import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// Use environment variable for preview, or current origin for production
const getApiUrl = () => {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  const hostname = window.location.hostname || '';
  // If we're on a production domain (emergent.host), use the current origin
  if (hostname.includes('emergent.host') || hostname.includes('emergent-')) {
    return window.location.origin;
  }
  return envUrl || window.location.origin;
};

const API_URL = getApiUrl();

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Retry function for cold start handling
const retryRequest = async (fn, retries = 3, delay = 1000) => {
  for (let i = 0; i < retries; i++) {
    try {
      return await fn();
    } catch (error) {
      const isLastAttempt = i === retries - 1;
      const isRetryable = !error.response || error.response.status >= 500 || error.code === 'ERR_NETWORK';
      
      if (isLastAttempt || !isRetryable) {
        throw error;
      }
      
      // Wait before retrying (exponential backoff)
      await new Promise(resolve => setTimeout(resolve, delay * (i + 1)));
    }
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const api = axios.create({
    baseURL: `${API_URL}/api`,
    withCredentials: true,
    timeout: 120000, // 120 second timeout for heavy reports
    headers: {
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache'
    }
  });

  api.interceptors.request.use((config) => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      config.headers.Authorization = `Bearer ${storedToken}`;
    }
    return config;
  });

  // Add response interceptor for automatic retry on cold start errors
  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const config = error.config;
      
      // Only retry on network errors or 5xx errors, and only if not already retried
      if (!config._retry && (!error.response || error.response.status >= 500 || error.code === 'ERR_NETWORK')) {
        config._retry = true;
        config._retryCount = (config._retryCount || 0) + 1;
        
        // Max 2 retries
        if (config._retryCount <= 2) {
          // Wait before retrying (exponential backoff)
          await new Promise(resolve => setTimeout(resolve, 1000 * config._retryCount));
          return api(config);
        }
      }
      
      return Promise.reject(error);
    }
  );

  const checkAuth = useCallback(async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
      return response.data;
    } catch (error) {
      setUser(null);
      setToken(null);
      localStorage.removeItem('token');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    // Clear any existing tokens before login to avoid conflicts on mobile
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    
    const response = await api.post('/auth/login', { email, password });
    const { token: newToken, user: userData } = response.data;
    
    // Store the new token
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    
    return userData;
  };

  const register = async (email, password, name, restaurantName) => {
    const response = await api.post('/auth/register', {
      email,
      password,
      name,
      restaurant_name: restaurantName,
    });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const processOAuthSession = async (sessionId) => {
    const response = await api.post('/auth/session', { session_id: sessionId });
    const { user: userData, session_token } = response.data;
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const updateProfile = async (profileData) => {
    const response = await api.put('/auth/profile', profileData);
    const { user: updatedUser } = response.data;
    setUser(updatedUser);
    return updatedUser;
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    processOAuthSession,
    checkAuth,
    updateProfile,
    api,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
